/**
 * Post-op auto-send — turning a failed API call into something an office (or a patient) can act on. Pure.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE, with NAMED exports only — cypress/node-tests/post-op-editor.test.js executes it.
 *
 * THE REAL BODIES (forms-service/post-op-api.js; createErrorResponse is `body: JSON.stringify(message)`, so a handler
 * that passes a STRING sends a bare JSON string and axios hands it back as `err.response.data === '…'`):
 *   savePostOpSettings      (PUT /save-forms-reminder-settings)
 *                           422 { code:'INVALID_SETTINGS', errors:[{ field, message }] }
 *                           409 { code:'ENABLE_BLOCKED', reason, message }     ← show the server's `message`
 *                           401 'Authorization header required' | 'Invalid or expired token'
 *                           403 'Token siteID does not match body siteID' | 'Forms is not enabled for this site.'
 *                           500 a bare string (err.message — never shown raw: it can name a column or a driver)
 *   postOpAcknowledge       (POST /get-long-url/{urlID})
 *                           200 { ok:true } | 404/403 { code } | 429 { code:'RATE_LIMITED' }
 *                           503 { code:'UNAVAILABLE' } (a burst of taps beat this one — tap again) | 500 { code:'UNAVAILABLE' }
 */

const NETWORK_MESSAGE = 'Couldn’t reach the server. Check your connection and try again.';
const AUTH_MESSAGE = 'Your sign-in expired. Reload the page and try again.';
const FORBIDDEN_MESSAGE = 'You don’t have permission to change this.';
const GENERIC_MESSAGE = 'Something went wrong. Please try again.';

/** A server string safe to put in front of a person: short, no markup. */
function presentable(value) {
  if (typeof value !== 'string') return '';
  const text = value.trim();
  return text && text.length <= 300 && text.indexOf('<') === -1 ? text : '';
}

function normalizeFieldErrors(list) {
  return (Array.isArray(list) ? list : [])
    .filter((e) => e && typeof e === 'object')
    .map((e) => ({ field: String(e.field || ''), message: presentable(e.message) || 'Invalid value.' }));
}

/**
 * @param {*} err  an axios-style error ({ response:{ status, data }, message })
 * @param {string} [fallback]  the sentence for an unrecognised failure
 * @returns {{ kind:'network'|'invalid'|'blocked'|'auth'|'forbidden'|'server', status:number|null, code:string, message:string,
 *             errors:Array<{field:string,message:string}>, reason:string }}
 */
export function describePostOpError(err, fallback) {
  const res = err && err.response;
  const base = { status: null, code: '', message: '', errors: [], reason: '' };
  if (!res || typeof res.status !== 'number') {
    return Object.assign(base, { kind: 'network', message: NETWORK_MESSAGE });
  }
  const data = res.data;
  const body = data && typeof data === 'object' ? data : {};
  const out = Object.assign(base, { status: res.status, code: typeof body.code === 'string' ? body.code : '' });

  if (res.status === 422 && out.code === 'INVALID_SETTINGS') {
    out.kind = 'invalid';
    out.errors = normalizeFieldErrors(body.errors);
    out.message = out.errors.length > 0 ? out.errors[0].message : 'Some of the message settings are not valid.';
    return out;
  }
  if (res.status === 409 && out.code === 'ENABLE_BLOCKED') {
    out.kind = 'blocked';
    out.reason = typeof body.reason === 'string' ? body.reason : '';
    out.message = presentable(body.message) || 'Auto-send can’t be turned on yet.';
    return out;
  }
  if (res.status === 401) return Object.assign(out, { kind: 'auth', message: AUTH_MESSAGE });
  if (res.status === 403) return Object.assign(out, { kind: 'forbidden', message: presentable(data) || FORBIDDEN_MESSAGE });
  return Object.assign(out, { kind: 'server', message: fallback || GENERIC_MESSAGE });
}

/**
 * What the patient's "I've read these instructions" tap should do after a FAILED acknowledge.
 *   'retry'        a network failure, 429, 500, 503 — "please tap again" (the server released/never took the claim)
 *   'unavailable'  404 / 403 / other 4xx — tapping again cannot help; point them at the office
 */
export function ackFailureKind(status) {
  if (typeof status !== 'number' || status === 0) return 'retry';
  if (status === 429 || status >= 500) return 'retry';
  return 'unavailable';
}
